import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  Building2, User, GraduationCap, Landmark, CheckCircle, Loader2, AlertTriangle, ArrowLeft, ArrowRight
} from 'lucide-react';

import { API_BASE as API_URL } from '../lib/apiBase';
const STEPS = ['Personal Details', 'Qualifications', 'Banking & ID', 'Review & Submit'];

const GENDERS = ['Male', 'Female', 'Other'];
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const CATEGORIES = ['Teaching', 'Non-Teaching'];
const APPOINTMENT_TYPES = ['Permanent', '11-Month Contract', 'Temporary', 'Contractual', 'Visiting', 'Ad-hoc', 'Probation'];

function StepIndicator({ currentStep }) {
  return (
    <div className="flex items-center justify-center gap-1 mb-8" data-testid="step-indicator">
      {STEPS.map((step, i) => (
        <React.Fragment key={step}>
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
              i < currentStep ? 'bg-emerald-500 text-white' :
              i === currentStep ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' :
              'bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
            }`}>
              {i < currentStep ? <CheckCircle className="w-4 h-4" /> : i + 1}
            </div>
            <span className={`text-xs hidden sm:inline ${i === currentStep ? 'font-semibold text-slate-900 dark:text-white' : 'text-slate-400'}`}>
              {step}
            </span>
          </div>
          {i < STEPS.length - 1 && <div className={`w-8 h-0.5 mx-1 ${i < currentStep ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-slate-700'}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function Onboarding() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(null);

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    date_of_birth: '', gender: '', address: '', emergency_contact: '',
    blood_group: '', category: 'Teaching', appointment_type: 'Permanent',
    highest_qualification: '', experience_years: 0, date_of_joining: new Date().toISOString().slice(0, 10),
    pan_number: '', aadhar_number: '', bank_account: '', bank_name: '', ifsc_code: '',
    basic_salary: 0
  });

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/onboarding/${token}`);
        if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Invalid link'); }
        const data = await res.json();
        setInfo(data);
        setForm(f => ({
          ...f,
          first_name: data.candidate_name?.split(' ')[0] || '',
          last_name: data.candidate_name?.split(' ').slice(1).join(' ') || ''
        }));
      } catch (err) { setError(err.message); }
      finally { setLoadingInfo(false); }
    };
    fetchInfo();
  }, [token]);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const validateStep = () => {
    if (step === 0) {
      const req = ['first_name', 'last_name', 'email', 'phone', 'date_of_birth', 'gender', 'address'];
      const missing = req.filter(f => !form[f]);
      if (missing.length) { return `Missing: ${missing.join(', ').replace(/_/g, ' ')}`; }
    }
    return null;
  };

  const nextStep = () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setError(null);
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const prevStep = () => { setError(null); setStep(s => Math.max(s - 1, 0)); };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/onboarding/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Submission failed'); }
      setCompleted(await res.json());
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  // LOADING
  if (loadingInfo) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // INVALID LINK
  if (!info) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Invalid Link</h2>
            <p className="text-sm text-slate-500">{error || 'This onboarding link is invalid or has expired.'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // COMPLETED
  if (completed) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full" data-testid="onboarding-success">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold mb-2 text-slate-900 dark:text-white">Welcome Aboard!</h2>
            <p className="text-sm text-slate-500 mb-6">Your account has been created successfully.</p>
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 text-left space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Employee ID</span>
                <span className="font-mono font-bold" data-testid="emp-id">{completed.employee_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Login Email</span>
                <span className="font-medium" data-testid="emp-email">{completed.login_email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Temporary Password</span>
                <span className="font-mono font-bold text-red-600" data-testid="emp-password">{completed.temp_password}</span>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-4">Please save these credentials. You'll need them to log in.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // FORM
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="onboarding-page">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-6">
        <div className="max-w-2xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-10 h-10 bg-white rounded-md flex items-center justify-center p-1 shrink-0 border border-slate-200 dark:border-slate-700">
              <img src="/sgi-logo.jpg" alt="SGI" className="w-full h-full object-contain" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">SGI HRMS</h1>
          </div>
          <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-200">Employee Onboarding</h2>
          <p className="text-sm text-slate-500 mt-1">
            Welcome, <strong>{info.candidate_name}</strong>! You've been selected as <strong>{info.position}</strong> in <strong>{info.department}</strong>.
          </p>
          {info.entity_name && <p className="text-xs text-slate-400 mt-1">{info.entity_name}</p>}
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        <StepIndicator currentStep={step} />

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400" data-testid="form-error">
            {error}
          </div>
        )}

        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="p-6">
            {/* Step 0: Personal */}
            {step === 0 && (
              <div className="space-y-4" data-testid="step-personal">
                <div className="flex items-center gap-2 mb-4">
                  <User className="w-5 h-5 text-slate-600" />
                  <h3 className="font-semibold text-slate-800 dark:text-white">Personal Details</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>First Name *</Label>
                    <Input data-testid="input-first-name" value={form.first_name} onChange={e => set('first_name', e.target.value)} />
                  </div>
                  <div>
                    <Label>Last Name *</Label>
                    <Input data-testid="input-last-name" value={form.last_name} onChange={e => set('last_name', e.target.value)} />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input data-testid="input-email" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input data-testid="input-phone" value={form.phone} onChange={e => set('phone', e.target.value)} />
                  </div>
                  <div>
                    <Label>Date of Birth *</Label>
                    <Input data-testid="input-dob" type="date" value={form.date_of_birth} onChange={e => set('date_of_birth', e.target.value)} />
                  </div>
                  <div>
                    <Label>Gender *</Label>
                    <Select value={form.gender} onValueChange={v => set('gender', v)}>
                      <SelectTrigger data-testid="select-gender"><SelectValue placeholder="Select gender" /></SelectTrigger>
                      <SelectContent>
                        {GENDERS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Address *</Label>
                  <Textarea data-testid="input-address" value={form.address} onChange={e => set('address', e.target.value)} rows={2} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Emergency Contact</Label>
                    <Input data-testid="input-emergency" value={form.emergency_contact} onChange={e => set('emergency_contact', e.target.value)} />
                  </div>
                  <div>
                    <Label>Blood Group</Label>
                    <Select value={form.blood_group} onValueChange={v => set('blood_group', v)}>
                      <SelectTrigger data-testid="select-blood-group"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {BLOOD_GROUPS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: Qualifications */}
            {step === 1 && (
              <div className="space-y-4" data-testid="step-qualifications">
                <div className="flex items-center gap-2 mb-4">
                  <GraduationCap className="w-5 h-5 text-slate-600" />
                  <h3 className="font-semibold text-slate-800 dark:text-white">Qualifications & Employment</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Highest Qualification</Label>
                    <Input data-testid="input-qualification" value={form.highest_qualification} onChange={e => set('highest_qualification', e.target.value)} placeholder="e.g., PhD in Computer Science" />
                  </div>
                  <div>
                    <Label>Experience (years)</Label>
                    <Input data-testid="input-experience" type="number" min="0" value={form.experience_years} onChange={e => set('experience_years', parseInt(e.target.value) || 0)} />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={form.category} onValueChange={v => set('category', v)}>
                      <SelectTrigger data-testid="select-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Appointment Type</Label>
                    <Select value={form.appointment_type} onValueChange={v => set('appointment_type', v)}>
                      <SelectTrigger data-testid="select-appointment"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {APPOINTMENT_TYPES.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Date of Joining</Label>
                    <Input data-testid="input-doj" type="date" value={form.date_of_joining} onChange={e => set('date_of_joining', e.target.value)} />
                  </div>
                  <div>
                    <Label>Basic Salary (Monthly)</Label>
                    <Input data-testid="input-salary" type="number" min="0" value={form.basic_salary} onChange={e => set('basic_salary', parseFloat(e.target.value) || 0)} />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Banking */}
            {step === 2 && (
              <div className="space-y-4" data-testid="step-banking">
                <div className="flex items-center gap-2 mb-4">
                  <Landmark className="w-5 h-5 text-slate-600" />
                  <h3 className="font-semibold text-slate-800 dark:text-white">Banking & Identity</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>PAN Number</Label>
                    <Input data-testid="input-pan" value={form.pan_number} onChange={e => set('pan_number', e.target.value.toUpperCase())} placeholder="ABCDE1234F" />
                  </div>
                  <div>
                    <Label>Aadhar Number</Label>
                    <Input data-testid="input-aadhar" value={form.aadhar_number} onChange={e => set('aadhar_number', e.target.value)} placeholder="1234 5678 9012" />
                  </div>
                  <div>
                    <Label>Bank Account Number</Label>
                    <Input data-testid="input-bank-account" value={form.bank_account} onChange={e => set('bank_account', e.target.value)} />
                  </div>
                  <div>
                    <Label>Bank Name</Label>
                    <Input data-testid="input-bank-name" value={form.bank_name} onChange={e => set('bank_name', e.target.value)} />
                  </div>
                  <div>
                    <Label>IFSC Code</Label>
                    <Input data-testid="input-ifsc" value={form.ifsc_code} onChange={e => set('ifsc_code', e.target.value.toUpperCase())} placeholder="SBIN0001234" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Review */}
            {step === 3 && (
              <div className="space-y-4" data-testid="step-review">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle className="w-5 h-5 text-slate-600" />
                  <h3 className="font-semibold text-slate-800 dark:text-white">Review & Submit</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">Please verify your details before submitting.</p>

                <div className="space-y-3 text-sm">
                  <ReviewSection title="Personal" items={[
                    ['Name', `${form.first_name} ${form.last_name}`],
                    ['Email', form.email], ['Phone', form.phone],
                    ['DOB', form.date_of_birth], ['Gender', form.gender],
                    ['Address', form.address],
                  ]} />
                  <ReviewSection title="Qualifications" items={[
                    ['Qualification', form.highest_qualification],
                    ['Experience', `${form.experience_years} years`],
                    ['Category', form.category],
                    ['Appointment', form.appointment_type],
                    ['Joining Date', form.date_of_joining],
                  ]} />
                  <ReviewSection title="Banking" items={[
                    ['PAN', form.pan_number], ['Aadhar', form.aadhar_number],
                    ['Bank', `${form.bank_name} — ${form.bank_account}`],
                    ['IFSC', form.ifsc_code],
                  ]} />
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-4 border-t border-slate-200 dark:border-slate-700">
              <Button variant="outline" onClick={prevStep} disabled={step === 0} data-testid="btn-prev-step">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              {step < STEPS.length - 1 ? (
                <Button onClick={nextStep} data-testid="btn-next-step">
                  Next <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={submitting} data-testid="btn-submit-onboarding" className="bg-emerald-600 hover:bg-emerald-700">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Complete Onboarding
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ReviewSection({ title, items }) {
  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3">
      <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{title}</h4>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        {items.map(([label, val]) => (
          <div key={label} className="flex justify-between">
            <span className="text-slate-500">{label}:</span>
            <span className="font-medium text-slate-900 dark:text-white truncate ml-2">{val || '—'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
